import React, { useImperativeHandle } from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";
import {
	SCROLL_ZONE_VALUE,
	StyledScrollableContainer,
} from "./styling/ScrollableContainer.styles";
import { ColorPaletteOptions } from "../../../../../types/theming";
import { useEffect, useRef, useState } from "react";
import { BoxBaseProps } from "../BoxBase.types";
import { useUserAgent } from "next-useragent";

const resolveOverflow = (value: any) => {
	return value === "scroll" ? "auto" : value;
};

const ScrollableContainer = React.forwardRef<
	HTMLElement,
	BoxBaseProps<ColorPaletteOptions>
>(
	(
		{
			className,
			overflow,
			overflowX,
			overflowY,
			onMouseMove,
			onMouseLeave,
			onMouseDown,
			onMouseUp,
			...props
		},
		ref
	) => {
		const block = new BemIt("ScrollableContainer");
		const scrollableContainerRef = useRef<HTMLElement>(null);
		const [isActiveV, setIsActiveV] = useState(false);
		const [isActiveH, setIsActiveH] = useState(false);
		const [isMouseDown, setIsMouseDown] = useState(false);
		const [isFirefox, setIsFirefox] = useState(false);
		const userAgent = useUserAgent(window?.navigator?.userAgent || "");

		useImperativeHandle(
			ref,
			() => scrollableContainerRef?.current as HTMLElement
		);

		useEffect(() => {
			setIsFirefox(userAgent?.isFirefox);
		}, [userAgent?.isFirefox]);

		// Resolve overflow props
		overflowX = resolveOverflow(overflow || overflowX || "hidden");
		overflowY = resolveOverflow(overflow || overflowY || "hidden");

		const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
			if (!isFirefox) {
				const ele = scrollableContainerRef?.current;

				const shouldActivateScroll = (
					boundingRectPos: number,
					elementWidth: number,
					clientPos: number
				): boolean => {
					const distance = elementWidth - (clientPos - boundingRectPos);
					return distance > 0 && distance < SCROLL_ZONE_VALUE;
				};

				// Vertical scroll
				if (
					ele &&
					ele.clientHeight < ele.scrollHeight &&
					overflowY === "auto"
				) {
					const rect = ele.getBoundingClientRect();
					setIsActiveV(
						shouldActivateScroll(rect.left, ele.offsetWidth, event.clientX)
					);
				}

				// Horizontal scroll
				if (ele && ele.clientWidth < ele.scrollWidth && overflowX === "auto") {
					const rect = ele.getBoundingClientRect();
					setIsActiveH(
						shouldActivateScroll(rect.top, ele.offsetHeight, event.clientY)
					);
				}
			}

			if (onMouseMove) {
				onMouseMove(event);
			}
		};

		const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
			setIsActiveV(false);
			setIsActiveH(false);
			if (onMouseLeave) {
				onMouseLeave(event);
			}
		};

		const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
			setIsMouseDown(true);
			if (onMouseDown) {
				onMouseDown(event);
			}
		};

		const handleMouseUp = (event: React.MouseEvent<HTMLElement>) => {
			setIsMouseDown(false);
			if (onMouseUp) {
				onMouseUp(event);
			}
		};

		return (
			<StyledScrollableContainer
				className={addClass(className).after(
					block.mod({
						"active-v": isActiveV,
						"active-h": isActiveH,
						"mouse-down": isMouseDown,
					})
				)}
				ref={scrollableContainerRef}
				overflowX={overflowX}
				overflowY={overflowY}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
				isFirefox={isFirefox}
				{...props}
			>
				{props.children}
			</StyledScrollableContainer>
		);
	}
);

export default ScrollableContainer;
